import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch, apiSearch } from './client.ts';
import { NeedsSignIn, useSession } from '../auth/session.tsx';
import type {
  ClientBanner, ClientListGroup, ClientPage, CourtOrderInput, Me, PlacementPage, RecordedCourtOrder,
  RecordedVisit, TimelinePage, VisitContext, VisitInput, WorkPage,
  GeocodeQueue,
  TileCache,
  Unmappable,
} from './types.ts';

/**
 * Retry and staleness are set for the connection these tablets actually have:
 * a nursing home lobby on someone else's wifi. Keep showing the last good
 * answer, retry a few times with backoff, and let the screen say how old what
 * you are looking at is.
 */
export const queryDefaults = {
  staleTime: 30_000,
  gcTime: 30 * 60_000,
  refetchOnWindowFocus: true,

  /**
   * Retry the connection, never the answer.
   *
   * A 403 from `client_exists_but_hidden()` or a 404 is a decision the server
   * has already made; asking again four times cannot change it. It only
   * spends four round trips on the worst connection in the building and
   * delays the message by several seconds. Anything below 500 is final.
   */
  retry: (failureCount: number, error: Error) => {
    // An expired session is final too, and was not being treated as such:
    // NeedsSignIn is not an ApiError, so it fell through to the connection
    // rule and was retried four times with backoff — seven seconds of silence
    // before anyone was told to sign in again.
    if (error instanceof NeedsSignIn) return false;
    if (error instanceof ApiError && error.status < 500) return false;
    return failureCount < 3;
  },
} as const;

export function useMe() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<Me>('/me', getToken),
    ...queryDefaults,
    staleTime: 5 * 60_000,
  });
}

export function useClientBanner(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'banner'],
    queryFn: () => apiFetch<ClientBanner>(`/clients/${clientId}`, getToken),
    ...queryDefaults,
  });
}

export function useClientHistory(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'history'],
    queryFn: () =>
      apiSearch<PlacementPage>(`/clients/${clientId}/history`, { limit: 100 }, getToken),
    ...queryDefaults,
  });
}

/**
 * The client list.
 *
 * `placeholderData` keeps the previous page on screen while the next one
 * loads, so typing in the search box does not blank the list between
 * keystrokes — on the connection this is used over, a list that empties on
 * every character is unusable. The rows go stale for a moment instead, which
 * `Staleness` is already the pattern for elsewhere.
 */
export function useClientList(params: {
  q: string;
  sort: string;
  offset: number;
  limit: number;
  /** The toggle. Ignored by the server while a search is typed: a search spans every group. */
  group: ClientListGroup;
}) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['clients', params],
    queryFn: () => apiSearch<ClientPage>('/clients/search', params, getToken),
    ...queryDefaults,
    placeholderData: (previous) => previous,
  });
}

/**
 * The caller's open tasks, soonest due first; the page groups them.
 *
 * 200 is the endpoint's cap. A case manager with more open tasks than that
 * has a different problem, and the page says how many it is not showing.
 */
export function useMyWork() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['work', 'mine'],
    queryFn: () => apiSearch<WorkPage>('/me/work', { sort: 'due_date', limit: 200 }, getToken),
    ...queryDefaults,
  });
}

/** Placements and temporary stays in one sequence. */
export function useClientTimeline(clientId: string, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'timeline'],
    queryFn: () =>
      apiSearch<TimelinePage>(`/clients/${clientId}/timeline`, { limit: 100 }, getToken),
    enabled,
    ...queryDefaults,
  });
}

/**
 * Where the client was on a given date.
 *
 * Keyed by the date, so changing the visit date in the form re-asks the
 * question rather than carrying a stale answer — a visit written up a
 * fortnight late must resolve against that day, not today.
 */
export function useVisitContext(clientId: string, date: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'where', date],
    queryFn: () =>
      apiFetch<VisitContext>(`/clients/${clientId}/where?date=${date}`, getToken),
    ...queryDefaults,
    enabled: date.length === 10,
  });
}

/**
 * Record a court order.
 *
 * The response is kept by the caller rather than folded into the cache: it is
 * the list of obligations this order created, which nothing else on the page
 * shows. The client's queries are invalidated because the order changed the
 * scope the header shows.
 */
export function useRecordCourtOrder(clientId: string) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CourtOrderInput) =>
      apiFetch<RecordedCourtOrder>('/court_orders', getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void queryClient.invalidateQueries({ queryKey: ['work'] });
    },
  });
}

/**
 * Record a visit.
 *
 * On success the client's queries are invalidated rather than patched: a visit
 * closes the task that asked for it, by a trigger in the schema, so the server
 * knows things about the consequences that this mutation does not.
 */
export function useRecordVisit(clientId: string) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (visit: VisitInput) =>
      apiFetch<RecordedVisit>('/visits', getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(visit),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      void queryClient.invalidateQueries({ queryKey: ['visits'] });
    },
  });
}

/**
 * The geocoding backlog (056). Administration only — the route refuses anyone
 * else, and the page reports that refusal rather than rendering an empty list.
 *
 * `refetchInterval` while work is outstanding: the worker runs outside the web
 * app, so the only way this page learns that the queue is draining is to ask
 * again. It stops polling once nothing is left, so an idle tab is idle.
 */
/**
 * `POST /geocoding/run` — drain the queue now. The API answers 202 and runs it
 * in the background (a full drain is minutes), so success means "started";
 * the outcome arrives through the backlog's `worker` line, which is refetched.
 */
export function useRunGeocoder() {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ started: boolean; queued: number; trigger: string }>('/geocoding/run', getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['geocode-backlog'] });
    },
  });
}

export function useGeocodeBacklog(state: string, limit = 100) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['geocode-backlog', state, limit],
    queryFn: () =>
      apiFetch<GeocodeQueue>(
        `/geocoding/backlog?state=${encodeURIComponent(state)}&limit=${limit}`,
        getToken,
      ),
    ...queryDefaults,
    refetchInterval: (query) => {
      const data = query.state.data as GeocodeQueue | undefined;
      // A run under way is followed closely, so its progress moves on screen.
      if (data?.status?.running) return 3_000;
      const outstanding = data?.by_state
        ?.filter((s) => s.state !== 'Resolved' && s.state !== 'Given up')
        .reduce((n, s) => n + s.n, 0);
      return outstanding && outstanding > 0 ? 15_000 : false;
    },
  });
}

export function useUnmappable() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['geocode-unmappable'],
    queryFn: () => apiFetch<Unmappable>('/geocoding/unmappable', getToken),
    ...queryDefaults,
    staleTime: 5 * 60_000,
  });
}

/**
 * The tile cache's size, for the geocoding backlog page.
 *
 * It is on that page because a limit nobody can see is a limit nobody notices
 * hitting — and a directory that only grows is the same shape as the audit
 * partitions that ran out in 2028.
 */
export function useTileCache() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['tile-cache'],
    queryFn: () => apiFetch<TileCache>('/tiles/cache', getToken),
    ...queryDefaults,
    staleTime: 60_000,
    // Administration-only, like the page it sits on. A 403 here is not worth
    // retrying and not worth showing: the panel simply does not appear.
    retry: false,
  });
}
